import axios from "axios";
import { newBaseUrl } from "../redux/constants/const";
import { tokenService, sessionExpired } from "../services/tokenService";

export { sessionExpired };

export const getErrorMessage = (error) => {
  return (
    error?.response?.data?.message ??
    error?.response?.data?.data?.message ??
    error?.response?.data?.error ??
    error?.message ??
    "An error occurred"
  );
};

export const eAPIResponseStatus = {
  SUCCESS: "SUCCESS",
  FAILURE: "FAILURE",
};

// ---------------------------------------------------------------------------
// JWT helpers — decode expiry without an external library
// ---------------------------------------------------------------------------

const getTokenExpiry = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1])).exp * 1000;
  } catch {
    return 0;
  }
};

const isTokenExpiringSoon = (token) =>
  getTokenExpiry(token) - Date.now() < 60_000;

// ---------------------------------------------------------------------------
// Shared Axios instance
// ---------------------------------------------------------------------------

const axiosInstance = axios.create({
  timeout: 70000,
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Refresh state — deduplication + subscriber queue
// ---------------------------------------------------------------------------

let _isRefreshing = false;
let _refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => _refreshSubscribers.push(cb);

const notifySubscribers = (payload) => {
  _refreshSubscribers.forEach((cb) => cb(payload));
  _refreshSubscribers = [];
};

// ---------------------------------------------------------------------------
// Silent refresh — single in-flight call, notifies all queued subscribers.
// The refresh token is sent automatically via the HttpOnly cookie.
// ---------------------------------------------------------------------------

export const silentRefresh = () => {
  if (_isRefreshing) {
    return new Promise((resolve) => subscribeTokenRefresh(resolve));
  }

  _isRefreshing = true;

  return axios({
    method: "post",
    url: `${newBaseUrl}web/auth/refresh`,
    headers: { "Content-Type": "application/json" },
    data: {},
    withCredentials: true,
    timeout: 15000,
  })
    .then((res) => {
      const data = res?.data;
      if (data?.accessToken) {
        tokenService.setAccessToken(data.accessToken);
        notifySubscribers(data);
        return data;
      }
      notifySubscribers(null);
      return null;
    })
    .catch(() => {
      notifySubscribers(null);
      return null;
    })
    .finally(() => {
      _isRefreshing = false;
    });
};

// ---------------------------------------------------------------------------
// Request interceptor — proactive refresh + attach Bearer token
// ---------------------------------------------------------------------------

axiosInstance.interceptors.request.use(
  async (config) => {
    if (config._isRefresh) return config;

    const token = tokenService.getAccessToken();
    if (!token) return config;

    if (isTokenExpiringSoon(token)) {
      const refreshResult = await silentRefresh();
      if (refreshResult?.accessToken) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${refreshResult.accessToken}`;
      } else {
        sessionExpired();
        return Promise.reject(new Error("Session expired"));
      }
    } else {
      const hasAuth =
        config.headers?.Authorization || config.headers?.authorization;
      if (!hasAuth) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — queue-based 401 handling as a safety net
// ---------------------------------------------------------------------------

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error.config;

    const is401 =
      error?.response?.status === 401 &&
      !originalConfig?._retried &&
      !originalConfig?._isRefresh;

    const sentBearer =
      typeof (
        originalConfig?.headers?.Authorization ??
        originalConfig?.headers?.authorization
      ) === "string" &&
      /^\s*bearer\s+/i.test(
        originalConfig?.headers?.Authorization ??
          originalConfig?.headers?.authorization,
      );

    if (is401 && sentBearer) {
      originalConfig._retried = true;

      const refreshResult = await silentRefresh();
      if (refreshResult?.accessToken) {
        originalConfig.headers.Authorization = `Bearer ${refreshResult.accessToken}`;
        return axiosInstance(originalConfig);
      }

      sessionExpired();
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const requestSentBearerToken = (config) => {
  const headers = config?.headers;
  if (!headers || typeof headers !== "object") return false;
  const auth = headers.Authorization ?? headers.authorization;
  return typeof auth === "string" && /^\s*bearer\s+/i.test(auth);
};

// ---------------------------------------------------------------------------
// Public API helpers
// ---------------------------------------------------------------------------

export const apiCallWithRefresh = (config) => axiosInstance(config);

export const apiCall = async (config) => {
  const response = await axiosInstance(config);
  const body = response?.data;

  const isTokenError =
    body?.status === false &&
    typeof body?.message === "string" &&
    body.message.toLowerCase().includes("token") &&
    requestSentBearerToken(config) &&
    !config?._retried;

  if (isTokenError) {
    const refreshResult = await silentRefresh();
    if (refreshResult?.accessToken) {
      return axiosInstance({
        ...config,
        _retried: true,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${refreshResult.accessToken}`,
        },
      });
    }
    sessionExpired();
  }

  return response;
};
